import { differenceInHours, format, isValid, parse, parseISO } from 'date-fns';

export function tryParseISO(time: string | undefined | null): Date | undefined {
  if (!time) {
    return undefined;
  }

  const datetime = parseISO(time);

  if (!isValid(datetime)) {
    return;
  }

  return datetime;
}

export function tryParse(
  dateString: string | undefined | null,
  formatString: string,
  referenceDate: number | Date,
): Date | undefined {
  if (!dateString) {
    return;
  }

  const datetime = parse(dateString, formatString, referenceDate);

  if (!isValid(datetime)) {
    return;
  }

  return datetime;
}

export interface FormatDatetimeOptions {
  /**
   * 原始字符串格式
   *
   * 当原始时间字符串为非 ISO8601 格式时需要指定
   */
  sourceFormat?: string;

  /**
   * 参考时间
   *
   * 当原始时间字符串为非 ISO8601 格式时需要指定
   *
   * 默认为当前时间
   */
  referenceDate?: number | Date;
}

/**
 * 格式化输入时长
 * @param date 到期时间
 * @param baseDate 基准时间
 * @returns
 */
export function formatDistance(date: number | Date, baseDate: number | Date) {
  /**
   * 总小时数
   */
  const totalHours = Math.abs(differenceInHours(date, baseDate));

  /**
   * 小时的部分
   */
  const hours = totalHours % 24;
  /**
   * 天的部分
   */
  const days = Math.floor(totalHours / 24);

  const daysPart = `${days}天`;
  const hoursPart = `${hours}小时`;

  if (days === 0) {
    return hoursPart;
  }

  if (hours === 0) {
    return daysPart;
  }

  return daysPart + hoursPart;
}

/**
 * 将时间格式化为指定的格式
 *
 * @param value 需要格式化的时间
 * @param formatString 时间格式，默认为 yyyy-MM-dd HH:mm:ss
 * @param options 格式化配置
 * @returns
 */
export const formatDatetime = (
  value: Date | string | number,
  formatString = 'yyyy-MM-dd HH:mm:ss',
  options?: FormatDatetimeOptions,
) => {
  let date: Date | number | undefined;

  if (value instanceof Date) {
    date = value;
  }

  if (typeof value === 'number') {
    date = new Date(value);
  }

  if (typeof value === 'string') {
    if (options?.sourceFormat) {
      date = parse(value, options.sourceFormat, new Date());
    } else {
      date = parseISO(value);
    }
  }

  if (!date) {
    return null;
  }

  if (!isValid(date)) {
    return null;
  }

  return format(date, formatString);
};
